import { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Turnstile from "@/components/Turnstile";
import AuthShell from "@/components/AuthShell";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState("");
  const [resetSignal, setResetSignal] = useState(0);

  const handleToken = useCallback((t) => setToken(t), []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(form.email, form.password, { turnstile_token: token });
      navigate(user.role === "admin" ? "/admin" : "/account");
    } catch (e2) {
      setError(errMsg(e2));
      setToken("");
      setResetSignal((s) => s + 1);
    } finally {
      setLoading(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthShell
      testid="login-page"
      logoTestid="login-logo"
      eyebrow="Welcome back"
      title={<>Sign <span className="italic gold-text">in</span></>}
      sub="Your bookings, loyalty perks and express check-in — all in one place."
      footer={
        <p className="mt-8 text-base text-muted-foreground font-ui">
          No account? <Link data-testid="login-register-link" to="/register" className="gold-text hover:underline">Create one</Link> — or book as a guest.
        </p>
      }
    >
      <form onSubmit={submit} className="mt-10 space-y-7">
        <div>
          <Label className="font-ui text-sm tracking-wide text-muted-foreground">Email</Label>
          <Input data-testid="login-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-dark mt-2 h-12 text-base rounded-2xl px-4" />
        </div>
        <div>
          <Label className="font-ui text-sm tracking-wide text-muted-foreground">Password</Label>
          <Input data-testid="login-password-input" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input-dark mt-2 h-12 text-base rounded-2xl px-4" />
        </div>
        <div className="text-right -mt-4">
          <Link data-testid="forgot-password-link" to="/forgot-password" className="text-sm font-ui text-muted-foreground hover:text-primary transition-colors duration-200">Forgot password?</Link>
        </div>
        <div>
          <Label className="font-ui text-sm tracking-wide text-muted-foreground">Security check</Label>
          <div className="mt-2">
            <Turnstile action="login" onToken={handleToken} resetSignal={resetSignal} />
          </div>
        </div>
        {error && <p data-testid="login-error" className="text-base text-destructive font-ui">{error}</p>}
        <Button data-testid="login-submit-button" disabled={loading || !token} className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui h-12 text-base tracking-wide transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(199,30,46,0.35)]">
          {loading ? "Signing in…" : "Sign In"}
        </Button>
      </form>
      <div className="mt-8">
        <div className="flex items-center gap-4 text-sm text-muted-foreground font-ui">
          <div className="h-px flex-1 bg-black/10" />or<div className="h-px flex-1 bg-black/10" />
        </div>
        <button data-testid="google-signin-button" type="button" onClick={googleSignIn} className="mt-5 w-full rounded-full border border-border bg-black/5 hover:bg-black/10 transition-colors duration-200 h-12 flex items-center justify-center gap-3 text-base font-ui">
          <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
            <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z" />
            <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z" />
            <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
          </svg>
          Continue with Google
        </button>
      </div>
    </AuthShell>
  );
}
